import React, { useEffect, useState } from 'react'
import { Routes, Route, Navigate, Outlet } from 'react-router-dom'
import CollectionsLibrary from 'modules/Collections/index'
import AllProgramsLibrary from 'modules/Programs/components/AllProgramsLibrary'
import EditProgram from 'modules/Programs/components/ProgramView'
import DayLargeView from 'modules/Programs/components/DayLargeView'
import Library from 'modules/ExerciseLibrary'
import Account from 'modules/Account/Profile'
import Branding from 'modules/Account/Branding'
import Advanced from 'modules/Account/Advanced'
import Layout from 'modules/Layout/index'
import Progressions from 'modules/Progressions'
import Header, { CONTENT_STATS, LEGACY_REPORTS, REPORTS } from 'modules/App/components/Header'
import ProgramGroups from 'modules/ProgramGroups/index'
import LinkHub from 'modules/LinkHub'
import Community from 'modules/Community'
import CommunityModeration from 'modules/Community/Moderation'
import FeaturedResources from 'modules/FeaturedResources'
import TrybeAdmin from 'modules/Account/TrybeAdmin'
import HelpCenter from 'modules/HelpCenter/index'
import HelpCenterPage from 'modules/HelpCenter/helpCenterPage'
import VideoLibrary from 'modules/VideoLibrary/index'
import VideoViewer from 'modules/VideoViewer/index'
import CheckoutPageBuilderSubs from 'modules/PageBuilder/pages/CheckoutPageBuilderSubs'
import SalesPageBuilderSubs from 'modules/PageBuilder/pages/SalesPageBuilderSubs'
import AppOnboardingQuiz from 'modules/AppOnboardingQuiz/index'
import PageNotFound from './components/PageNotFound'
import { appRouterContainerId } from './constants/cssSelectorConstants'
import Login from 'modules/Auth/components/Login'
import Signup from 'modules/Auth/components/Signup'
import useAuth from 'modules/Auth/AuthContext'
import UnderMaintenance from 'modules/Maintenance/index'
import { useListenIsAdminQuery, useListenIsUnderMaintenanceQuery } from 'modules/Maintenance/maintenanceApi'
import { useListenStripePubKeyQuery } from 'modules/ProductInfo/productInfoApi'
import { useListenUserProfileQuery } from 'modules/Users/userApi'
import NewCoachOrgQuestionnaire from 'modules/NewCoachOrgQuestionnaire/index'
import ProductInfo from 'modules/ProductInfo/pages/ProductInfo'
import Pricing from 'modules/ProductInfo/pages/Pricing'
import Coupons from 'modules/ProductInfo/pages/Coupons'
import Sell from 'modules/ProductInfo/pages/Sell'
import OneTimeProduct from 'modules/ProductInfo/pages/OneTimeProduct'
import Reports from 'modules/Reports'
import ContentStats from 'modules/ContentStats'
import Share from 'modules/ProductInfo/pages/Share'
import { useCanSeeReports } from 'modules/Reports/useCanSeeReports'
import { useCanSeeContentStats } from 'modules/ContentStats/useCanSeeContentStats'
import Loading from 'modules/App/components/Loading'
import { useCustomization } from 'common/contexts/Customization/useCustomization'
import { maybeInitContentTags } from './utils/initContentTags'

export const ROUTES = {
  ADVANCED: 'advanced',
}

function ProtectedRoute() {
  const { userId, fbAuthed, coachOrgId } = useAuth()
  const { exVidOrientation, enableLandscapeExAudio } = useCustomization()

  const isAuthed = userId && fbAuthed
  const coachOrgIdLoading = coachOrgId === undefined || coachOrgId?.isLoading

  // Temp code needed to convert standalone workout tags to content tags
  // TEMP CODE START
  const [initializingTags, setInitializingTags] = useState(false)

  useEffect(() => {
    if (isAuthed && !coachOrgIdLoading && coachOrgId) {
      setInitializingTags(true)
      maybeInitContentTags(coachOrgId).then(() => {
        setInitializingTags(false)
      })
    }
  }, [isAuthed, coachOrgIdLoading, coachOrgId])
  // TEMP CODE END

  if (!isAuthed) {
    return <Navigate to='/login' replace />
  }

  if (!exVidOrientation || coachOrgIdLoading || enableLandscapeExAudio === null || initializingTags) {
    return <Loading />
  }

  if (!coachOrgId) {
    return <NewCoachOrgQuestionnaire />
  }

  return (
    <div>
      <Header />
      <Outlet />
    </div>
  )
}

function UnprotectedRoute() {
  const { userId, fbAuthed } = useAuth()

  return userId && fbAuthed ? (
    <Navigate to='/' replace />
  ) : (
    <div>
      <Outlet />
    </div>
  )
}

function AppRouter() {
  const { userId } = useAuth()
  const { data: isUnderMaintenance } = useListenIsUnderMaintenanceQuery()
  const { data: isAdmin } = useListenIsAdminQuery({ userId })
  const { data: profile } = useListenUserProfileQuery({ userId })
  const coachOrgId = profile?.coachOrgId || ''
  const { data: stripePubKey } = useListenStripePubKeyQuery({ coachOrgId })
  const stripePubKeyLoading = stripePubKey === undefined || stripePubKey?.isLoading
  const stripePubKeyExists = !stripePubKeyLoading && Boolean(stripePubKey)
  const showReports = useCanSeeReports()
  const contentStatsAccess = useCanSeeContentStats()

  if (isUnderMaintenance && !isAdmin) {
    return (
      <div className='flex flex-col h-screen' id={appRouterContainerId}>
        <div className='flex-1'>
          <UnderMaintenance />
        </div>
      </div>
    )
  }

  return (
    <div className='flex flex-col h-screen' id={appRouterContainerId}>
      <div className='flex-1'>
        <Routes>
          <Route element={<UnprotectedRoute />}>
            <Route path='/login' element={<Login />} />
            <Route path='/signup' element={<Signup />} />
          </Route>

          <Route element={<ProtectedRoute />}>
            <Route path='/' element={<Navigate to='/layout' replace />} />

            {/*PROGRAMS*/}
            <Route path='programs' element={<AllProgramsLibrary />} />
            <Route path='programs/:id' element={<EditProgram />} />
            <Route path='programs/:id/workouts/:workoutId' element={<DayLargeView />} />
            <Route path='/series' element={<ProgramGroups />} />

            {/*COLLECTIONS*/}
            <Route path='/collections' element={<CollectionsLibrary />} />

            {/*EXERCISES*/}
            <Route path='/exercises' element={<Library />} />
            <Route path='/progressions' element={<Progressions />} />
            <Route path='/videos' element={<VideoLibrary />} />

            {/*SETTINGS*/}
            <Route path='/account' element={<Account />} />
            <Route path='/branding' element={<Branding />} />
            <Route path={`/${ROUTES.ADVANCED}`} element={<Advanced />} />
            <Route path='/trybeadmin' element={<TrybeAdmin />} />

            {/*LAYOUT*/}
            <Route path='/layout' element={<Layout />} />
            <Route path='/layout/:tabId' element={<Layout />} />
            <Route path='/resources' element={<LinkHub />} />
            <Route path='/featured' element={<FeaturedResources />} />
            <Route path='/community' element={<Community />} />
            <Route path='/community/moderation' element={<CommunityModeration />} />
            <Route path='/quiz' element={<AppOnboardingQuiz />} />

            {/*SELL*/}
            {/* Setup is where a coach connects Stripe in the first place, so it has to be
                reachable before the org has a publishable key. The rest of Sell only makes
                sense once it does. */}
            <Route path='/sell/setup' element={<ProductInfo />} />
            {stripePubKeyExists && (
              <>
                <Route path='/sell' element={<Sell />} />
                <Route path='/sell/product/:productId' element={<OneTimeProduct />} />
                <Route path='/sell/pricing' element={<Pricing />} />
                <Route path='/sell/coupons' element={<Coupons />} />
                <Route path='/sell/salesPage' element={<SalesPageBuilderSubs />} />
                <Route path='/sell/checkoutPage' element={<CheckoutPageBuilderSubs />} />
              </>
            )}
            {/* Links to Sell from anywhere in the app land on setup until Stripe is connected. */}
            {!stripePubKeyLoading && !stripePubKeyExists && (
              <Route path='/sell/*' element={<Navigate to='/sell/setup' replace />} />
            )}

            {/* Every coach can open Share, even before Stripe is set up, so a link to it
                always lands somewhere that explains what to do next. */}
            <Route path='/share' element={<Share />} />

            {/*REPORTS*/}
            {showReports && <Route path={REPORTS} element={<Reports />} />}
            {showReports && <Route path={LEGACY_REPORTS} element={<Navigate to={REPORTS} replace={true} />} />}
            {/* Coaches without Content Stats switched on are sent to Attribution */}
            {showReports && !contentStatsAccess.isLoading && (
              <Route
                path={CONTENT_STATS}
                element={contentStatsAccess.canSee ? <ContentStats /> : <Navigate to={REPORTS} replace={true} />}
              />
            )}

            {/*OTHER*/}
            <Route path='/videoviewer' element={<VideoViewer />} />
            <Route path='/help' element={<HelpCenter />} />
            <Route path='/help/:contentId' element={<HelpCenterPage />} />

            {/*PAGE NOT FOUND*/}
            <Route path='*' element={<PageNotFound />} />
          </Route>
        </Routes>
      </div>
    </div>
  )
}

export default AppRouter
